import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { SearchResult } from './search.service';
import { AuthService } from 'providers/auth.service';

const STORAGE_KEY_PREFIX = 'chargeye_search_history';
const MAX_HISTORY_ITEMS = 10;

export interface SearchHistoryItem {
    result: SearchResult;
    timestamp: number;
}

@Injectable({
    providedIn: 'root',
})
export class SearchHistoryService {
    private history$ = new BehaviorSubject<SearchHistoryItem[]>([]);
    private currentStorageKey: string | null = null;
    private authService = inject(AuthService);

    constructor() {
        this.authService.profile$
            .pipe(
                map((profile) => (profile ? { sub: profile.sub, tenantId: profile.tenantId } : null)),
                distinctUntilChanged((a, b) => a?.sub === b?.sub && a?.tenantId === b?.tenantId)
            )
            .subscribe((identity) => {
                if (identity?.sub && identity?.tenantId) {
                    this.currentStorageKey = `${STORAGE_KEY_PREFIX}_${identity.sub}_${identity.tenantId}`;
                } else {
                    this.currentStorageKey = null;
                }
                this.loadFromStorage();
            });
    }

    getHistory$(): Observable<SearchHistoryItem[]> {
        return this.history$.asObservable();
    }

    getHistory(): SearchHistoryItem[] {
        return this.history$.getValue();
    }

    addToHistory(result: SearchResult): void {
        if (result.type === 'no_results') {
            return;
        }

        const currentHistory = this.getHistory();
        const existingIndex = this.findExistingIndex(currentHistory, result);

        let updatedHistory: SearchHistoryItem[];

        if (existingIndex !== -1) {
            // Remove existing entry and add to top
            updatedHistory = [
                { result, timestamp: Date.now() },
                ...currentHistory.slice(0, existingIndex),
                ...currentHistory.slice(existingIndex + 1),
            ];
        } else {
            // Add new entry to top
            updatedHistory = [{ result, timestamp: Date.now() }, ...currentHistory];
        }

        // Limit to max items
        updatedHistory = updatedHistory.slice(0, MAX_HISTORY_ITEMS);

        this.history$.next(updatedHistory);
        this.saveToStorage(updatedHistory);
    }

    removeFromHistory(result: SearchResult): void {
        const currentHistory = this.getHistory();
        const existingIndex = this.findExistingIndex(currentHistory, result);

        if (existingIndex !== -1) {
            const updatedHistory = [
                ...currentHistory.slice(0, existingIndex),
                ...currentHistory.slice(existingIndex + 1),
            ];
            this.history$.next(updatedHistory);
            this.saveToStorage(updatedHistory);
        }
    }

    clearHistory(): void {
        this.history$.next([]);
        this.saveToStorage([]);
    }

    private findExistingIndex(history: SearchHistoryItem[], result: SearchResult): number {
        return history.findIndex((item) => this.isSameResult(item.result, result));
    }

    private isSameResult(a: SearchResult, b: SearchResult): boolean {
        if (a.type !== b.type) {
            return false;
        }

        switch (a.type) {
            case 'station': {
                const aId = a.globalSearchStation?.stationId ?? a.station?.data?.station?.stationId;
                const bId =
                    (b as typeof a).globalSearchStation?.stationId ?? (b as typeof a).station?.data?.station?.stationId;
                return aId != null && bId != null && aId === bId;
            }
            case 'connector': {
                const aStationId =
                    a.globalSearchConnector?.stationId ??
                    a.globalSearchConnector?.station?.stationId ??
                    a.connector?.stationId;
                const bStationId =
                    (b as typeof a).globalSearchConnector?.stationId ??
                    (b as typeof a).globalSearchConnector?.station?.stationId ??
                    (b as typeof a).connector?.stationId;
                const aEvseId = a.globalSearchConnector?.evseId ?? a.connector?.evseId;
                const bEvseId = (b as typeof a).globalSearchConnector?.evseId ?? (b as typeof a).connector?.evseId;
                return (
                    aStationId != null &&
                    bStationId != null &&
                    aEvseId != null &&
                    bEvseId != null &&
                    aStationId === bStationId &&
                    aEvseId === bEvseId
                );
            }
            case 'location': {
                const aId = a.globalSearchLocation?.locationUid ?? a.location?.id;
                const bId = (b as typeof a).globalSearchLocation?.locationUid ?? (b as typeof a).location?.id;
                return aId != null && bId != null && aId === bId;
            }
            case 'organization': {
                const aId = a.globalSearchOrganization?.tenantId;
                const bId = (b as typeof a).globalSearchOrganization?.tenantId;
                return aId != null && bId != null && aId === bId;
            }
            case 'ticket': {
                const aId = a.ticket?.ticketId;
                const bId = (b as typeof a).ticket?.ticketId;
                return aId != null && bId != null && aId === bId;
            }
            default:
                return false;
        }
    }

    private loadFromStorage(): void {
        if (!this.currentStorageKey) {
            this.history$.next([]);
            return;
        }
        try {
            const stored = localStorage.getItem(this.currentStorageKey);
            if (stored) {
                const parsed = JSON.parse(stored) as SearchHistoryItem[];
                this.history$.next(parsed);
            } else {
                this.history$.next([]);
            }
        } catch {
            // If localStorage is unavailable or data is corrupted, start fresh
            this.history$.next([]);
        }
    }

    private saveToStorage(history: SearchHistoryItem[]): void {
        if (!this.currentStorageKey) {
            return;
        }
        try {
            localStorage.setItem(this.currentStorageKey, JSON.stringify(history));
        } catch {
            // localStorage might be full or unavailable, silently fail
        }
    }
}
